import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Star, Trash2, Eye, EyeOff, Loader2, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

function formatDate(iso) {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '-'; }
}

export default function AdminReviewsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    if (user && user.role !== 'admin') { navigate('/dashboard'); return; }
    fetchReviews();
  }, [user, navigate]);

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/reviews`, { withCredentials: true });
      setReviews(res.data.reviews || []);
    } catch {
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const toggleApproval = async (reviewId) => {
    setActionLoading(reviewId);
    try {
      const res = await axios.put(`${API_URL}/api/admin/reviews/${reviewId}/toggle`, {}, { withCredentials: true });
      toast.success(res.data.message);
      setReviews(prev => prev.map(r => r.id === reviewId ? { ...r, approved: res.data.approved } : r));
    } catch {
      toast.error('Failed to update review');
    } finally {
      setActionLoading(null);
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm('Delete this review permanently?')) return;
    setActionLoading(reviewId);
    try {
      await axios.delete(`${API_URL}/api/admin/reviews/${reviewId}`, { withCredentials: true });
      toast.success('Review deleted');
      setReviews(prev => prev.filter(r => r.id !== reviewId));
    } catch {
      toast.error('Failed to delete review');
    } finally {
      setActionLoading(null);
    }
  };

  const purgeTestReviews = async () => {
    if (!window.confirm('Delete all non-seed reviews? Seed reviews will be preserved.')) return;
    try {
      const res = await axios.delete(`${API_URL}/api/admin/reviews`, { withCredentials: true });
      toast.success(res.data.message);
      fetchReviews();
    } catch {
      toast.error('Failed to purge reviews');
    }
  };

  const approvedCount = reviews.filter(r => r.approved).length;
  const pendingCount = reviews.filter(r => !r.approved).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050507] pt-24 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#FF4500] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050507] pt-24 pb-16 px-4" data-testid="admin-reviews-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF4500]">Admin</span>
            <h1 className="font-clash text-3xl sm:text-4xl font-medium text-white mt-1">Review Manager</h1>
            <div className="flex items-center gap-4 mt-2">
              <span className="text-sm text-emerald-400" data-testid="approved-count">
                {approvedCount} visible
              </span>
              <span className="text-sm text-white/40" data-testid="pending-count">
                {pendingCount} hidden
              </span>
            </div>
          </div>
          {pendingCount > 0 && (
            <Button
              onClick={purgeTestReviews}
              variant="outline"
              className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 gap-2 h-10 px-5 rounded-xl"
              data-testid="purge-test-reviews-btn"
            >
              <Trash2 className="w-4 h-4" />
              Purge Non-Seed Reviews
            </Button>
          )}
        </div>

        {reviews.length === 0 ? (
          <div className="bg-[#0c0c10] border border-white/[0.06] rounded-2xl p-12 text-center">
            <MessageSquare className="w-8 h-8 text-white/20 mx-auto mb-3" />
            <p className="text-white/30 text-sm">No reviews yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {reviews.map((r) => (
              <div
                key={r.id}
                className={`bg-[#0c0c10] border rounded-xl p-5 transition-colors ${
                  r.approved ? 'border-emerald-500/20' : 'border-white/[0.06]'
                }`}
                data-testid={`review-item-${r.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <Star key={s} className={`w-3.5 h-3.5 ${s <= r.rating ? 'text-amber-400 fill-amber-400' : 'text-white/10'}`} />
                        ))}
                      </div>
                      {r.source === 'seed' && (
                        <span className="text-[10px] uppercase tracking-wider text-blue-400 font-semibold bg-blue-500/10 px-2 py-0.5 rounded">Seed</span>
                      )}
                      {r.user_label && (
                        <span className="text-xs text-white/35">{r.user_label}</span>
                      )}
                      <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${
                        r.approved
                          ? 'text-emerald-400 bg-emerald-500/10'
                          : 'text-white/30 bg-white/[0.04]'
                      }`}>
                        {r.approved ? 'Visible' : 'Hidden'}
                      </span>
                    </div>
                    <p className="text-sm text-white/60 leading-relaxed">
                      {r.comment || <span className="italic text-white/25">No comment</span>}
                    </p>
                    <p className="text-xs text-white/20 mt-2">{formatDate(r.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleApproval(r.id)}
                      disabled={actionLoading === r.id}
                      className={`h-8 w-8 p-0 ${r.approved ? 'text-emerald-400 hover:text-emerald-300' : 'text-white/30 hover:text-white/60'}`}
                      title={r.approved ? 'Hide from slider' : 'Show on slider'}
                      data-testid={`toggle-review-${r.id}`}
                    >
                      {actionLoading === r.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : r.approved ? (
                        <Eye className="w-4 h-4" />
                      ) : (
                        <EyeOff className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteReview(r.id)}
                      disabled={actionLoading === r.id}
                      className="h-8 w-8 p-0 text-red-400/50 hover:text-red-400"
                      title="Delete review"
                      data-testid={`delete-review-${r.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
